import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import toast from 'react-hot-toast';
import { 
  ComputerDesktopIcon, 
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  TrashIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const SessionsPage = () => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
      const response = await axios.get(`${API_URL}/security/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Sessions:', response.data);
      setSessions(response.data.data.sessions);
    } catch (error) {
      console.error('Fetch sessions error:', error);
      toast.error('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const revokeSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to revoke this session?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
      await axios.delete(`${API_URL}/security/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Session revoked successfully');
      fetchSessions();
    } catch (error) {
      console.error('Revoke session error:', error);
      toast.error('Failed to revoke session');
    }
  };

  const revokeAllOtherSessions = async () => {
    if (!window.confirm('Are you sure you want to revoke all other sessions? This will log you out from all other devices.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
      await axios.delete(`${API_URL}/security/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('All other sessions revoked successfully');
      fetchSessions();
    } catch (error) {
      console.error('Revoke all sessions error:', error);
      toast.error('Failed to revoke sessions');
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <DevicePhoneMobileIcon className="h-8 w-8" />;
      case 'tablet':
        return <DeviceTabletIcon className="h-8 w-8" />;
      default:
        return <ComputerDesktopIcon className="h-8 w-8" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrentSessionToken = () => {
    return localStorage.getItem('token');
  };

  if (isLoading) {
    return (
      <AdminLayout title="Active Sessions">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Active Sessions">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Active Sessions</h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage your active sessions across different devices
            </p>
          </div>
          {sessions.length > 1 && (
            <button
              onClick={revokeAllOtherSessions}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Revoke All Other Sessions
            </button>
          )}
        </div>

        {/* Sessions List */}
        {sessions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No active sessions</h3>
            <p className="mt-1 text-sm text-gray-500">
              You don't have any active sessions.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sessions.map((session) => {
              const isCurrentSession = session.token === getCurrentSessionToken();
              
              return (
                <div
                  key={session.id}
                  className={`bg-white border rounded-lg p-6 hover:shadow-md transition-shadow ${
                    isCurrentSession ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                  }`}
                >
                  {/* Device Icon and Name */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`${
                        isCurrentSession ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {getDeviceIcon(session.deviceType)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {session.deviceName || 'Unknown Device'}
                        </h3>
                        {isCurrentSession && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Current Session
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Session Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Browser:</span>
                      <span>{session.browser || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">IP:</span>
                      <span className="font-mono text-xs">{session.ipAddress || 'Unknown'}</span>
                    </div>
                    {session.location && (
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="font-medium mr-2">Location:</span>
                        <span>{session.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Last Activity */}
                  <div className="flex items-center text-xs text-gray-500 mb-4">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    <span>Last active: {formatDate(session.lastActivity)}</span>
                  </div>

                  {/* Actions */}
                  {!isCurrentSession && (
                    <button
                      onClick={() => revokeSession(session.id)}
                      className="w-full flex items-center justify-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Revoke Session
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">About Sessions</h4>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Each session represents a login from a different device or browser</li>
            <li>Sessions expire after 30 days of inactivity</li>
            <li>Revoking a session will log you out from that device</li>
            <li>You cannot revoke your current session</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SessionsPage;
